/* eslint-disable react-refresh/only-export-components */
import { action, makeObservable, observable, observe } from 'mobx';
import * as signalR from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { StreamRegistration } from './StreamRegistration';
import { ServiceConnectedState } from './SystemEventSocket.Misc';
import type { IDisposable, IInitializable } from '../Types';
import { StoreBase } from '../Types/StoreBase';
import { SettingsStore } from '../Setup';
import { debugLog } from '../Stores/DebugLogStore';

export { ServiceConnectedState } from './SystemEventSocket.Misc';

export interface IStreamRegistration<T = any> extends IDisposable {
    subscribe: (subscriber: signalR.IStreamSubscriber<T>) => IStreamRegistration<T>;
    dispose: () => void;
}

export class WebsocketStore extends StoreBase implements IInitializable {
    private _settingsStore: SettingsStore;
    private _wsConnection: signalR.HubConnection | null = null;
    private _disposed = false;

    constructor(authStore: SettingsStore) {
        super();

        makeObservable(this, {
            serviceConnected: observable,
            serviceConnectedSet: action,
        });

        this._settingsStore = authStore;
    }

    private uninitializeSocket() {
        this._disposed = true;

        if (this._wsConnection) {
            this._wsConnection.stop();
            this._wsConnection = null;
        }
    }

    private createSocket(url: string) {
        const reconnectPolicy: signalR.IRetryPolicy = {
            nextRetryDelayInMilliseconds: (_retryContext: signalR.RetryContext) => 3000,
        };
        const socket = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: async () => await this._settingsStore.identitiesSetup.getLatestToken() ?? '',
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withHubProtocol(new MessagePackHubProtocol())
            .configureLogging(signalR.LogLevel.Warning)
            .withAutomaticReconnect(reconnectPolicy)
            .build();

        return socket;
    }

    private async validateIdentitiesConnection(): Promise<boolean> {
        try {
            if (!this._settingsStore.identitiesSetup.serverAddress.trim()) {
                console.log('No server address configured');
                return false;
            }

            if (!this._settingsStore.identitiesSetup.authToken) {
                console.log('No auth token available - need to authenticate first');
                return false;
            }

            const authToken = await this._settingsStore.identitiesSetup.getLatestToken();

            // Use a simple API call to verify the connection and token validity
            // Note: You might want to replace this with a dedicated health check endpoint
            await Promise.race([
                // Make a lightweight API call to test connectivity
                fetch(`${this._settingsStore.identitiesSetup.serverAddress.trim()}/api/health`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Identities connection timeout')), 5000)),
            ]);

            console.log('Identities connection validated successfully');
            return true;
        } catch (error) {
            console.log('Identities connection validation failed:', error);
            return false;
        }
    }

    private async connectSocket() {
        if (this._wsConnection) {
            return;
        }

        this.serviceConnectedSet(ServiceConnectedState.Connecting);

        while (!this._disposed) {
            if (this._disposed) {
                return;
            }

            // First validate identities connection
            console.log('Validating identities connection...');
            const identitiesValid = await this.validateIdentitiesConnection();

            if (!identitiesValid) {
                console.log('Identities connection invalid, retrying in 5 seconds...');
                this.serviceConnectedSet(ServiceConnectedState.Offline);
                await new Promise((resolve) => setTimeout(resolve, 5000));
                continue;
            }

            // Proceed with WebSocket connection
            const socketUrl = this._settingsStore.identitiesSetup.serverAddress + '/websockets/systemEventHub';

            console.log('Connecting to websocket at', socketUrl);

            const connection = (this._wsConnection = this.createSocket(socketUrl));

            connection.onclose((_error?: Error | undefined) => {
                debugLog.warn('WebSocket connection closed');
                this.serviceConnectedSet(ServiceConnectedState.Offline);
            });

            connection.onreconnecting((_error?: Error | undefined) => {
                debugLog.warn('WebSocket reconnecting...');
                this.serviceConnectedSet(ServiceConnectedState.Reconnecting);
            });

            connection.onreconnected((_connectionId?: string | undefined) => {
                debugLog.info('WebSocket reconnected');
                this.serviceConnectedSet(ServiceConnectedState.Online);
            });

            try {
                // Add timeout to prevent hanging on invalid URLs
                await Promise.race([
                    connection.start(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Connection timeout')), 10000)),
                ]);

                debugLog.info('WebSocket connected');
                this.serviceConnectedSet(ServiceConnectedState.Online);
                break;
            } catch (e: unknown) {
                debugLog.error(`WebSocket connection failed, retrying...`);
                console.log({ e });

                // Clean up the failed connection
                if (this._wsConnection) {
                    await this._wsConnection.stop();
                    this._wsConnection = null;
                }

                this.serviceConnectedSet(ServiceConnectedState.Offline);

                // Wait before retrying
                await new Promise((resolve) => setTimeout(resolve, 3000));
            }
        }
    }

    observeConnectionChange(callback: (state: ServiceConnectedState) => void) {
        return observe(this, 'serviceConnected', () => {
            callback(this.serviceConnected);
        });
    }

    streamRegister<T = any>(methodName: string, ...args: any[]): IStreamRegistration {
        return new StreamRegistration<T>(this, methodName, ...args);
    }

    stream<T = any>(methodName: string, ...args: any[]) {
        if (!this._wsConnection || this.serviceConnected !== ServiceConnectedState.Online) {
            return undefined;
        }

        return this._wsConnection?.stream<T>(methodName, ...args);
    }

    initialize() {
        console.log('Websocket Initializing');
        this.connectSocket();
    }

    dispose() {
        this.uninitializeSocket();
    }

    serviceConnected: ServiceConnectedState = ServiceConnectedState.Offline;

    serviceConnectedSet(newState: ServiceConnectedState) {
        this.serviceConnected = newState;
    }
}
